import {useCallback} from 'react';
import {useDispatch} from 'react-redux';

import isEmpty from 'lodash.isempty';
import {useCachedTable} from 'src/hooks/common-data-service/cached-table';
import {getPresetsList} from 'src/modules/entitlements/utils/getPresetsList';
import {getGrantsByIds} from 'src/openapi/admin/apis/EntitlementsGrantApi';
import {getPresets, getPresetsRelation} from 'src/openapi/admin/apis/EntitlementsPresetApi';
import {getResourcesByIds} from 'src/openapi/admin/apis/EntitlementsResourceApi';
import {getRoleNamesByIds} from 'src/openapi/admin/apis/EntitlementsRoleApi';
import {useAppDispatch} from 'src/store/hooks';

import {useAbortSignal} from '@pb/kit-hooks-use-abort-signal';

import {
    presetsStateSelector,
} from '../slices/presets-state.slice';
import {
    presetsActions,
    presetsDataSelector,
    presetsResultSelector,
} from '../slices/presets.slice';

import type {PresetsBuildResponse} from '../typedef';
import type {CacheKeys} from 'src/hooks/common-data-service/cached-table';
import type {EntitlementResource} from 'src/openapi/admin/models/EntitlementResource';
import type {Grant} from 'src/openapi/admin/models/Grant';
import type {PresetItem} from 'src/openapi/admin/models/PresetItem';
import type {PresetRelationItem} from 'src/openapi/admin/models/PresetRelationItem';
import type {Role} from 'src/openapi/admin/models/Role';
import type {SuccessPayload} from 'src/typedef';


type PresetsCacheKeys = CacheKeys<undefined, undefined, {preset?: PresetItem}>;

export const useGetPresets = () => {
    const apiDispatch = useAppDispatch();
    const dispatch = useDispatch();
    const {createAbortSignal} = useAbortSignal();

    const getAction = useCallback(async (cacheKeys: PresetsCacheKeys) => {
        const signal = createAbortSignal();
        let presets: PresetItem[] = [];
        let totalCount = 0;

        if (cacheKeys.filters.preset) {
            presets = [cacheKeys.filters.preset as PresetItem];
            totalCount = 1;
        } else {
            const presetsResponse = await apiDispatch(getPresets({
                pageNumber: cacheKeys.pagination.currentPage,
                pageSize: cacheKeys.pagination.pageSize,
            }, {signal}));

            if (presetsResponse.payload.error) {
                return Promise.resolve({type: '', payload: presetsResponse.payload});
            }

            presets = presetsResponse.payload.data.itemList;
            totalCount = presetsResponse.payload.data.totalCount;
        }

        const relationsResponse = await apiDispatch(getPresetsRelation({
            presetIds: presets.map(({id}) => id),
        }, {signal}));

        const items: PresetRelationItem[] = Object
            .entries(relationsResponse.payload.data)
            .reduce((acc, [key, value]) => {
                return [...acc, ...(value.length ? value : [{presetId: key}])];
            }, [] as PresetRelationItem[]);

        if (relationsResponse.payload.success) {
            return Promise.resolve({type: '', payload: {
                data: {items, totalCount, presets},
                success: true,
                error: false,
            } as SuccessPayload<PresetsBuildResponse>});
        } else {
            return Promise.resolve({type: '', payload: relationsResponse.payload});
        }
    }, [apiDispatch, createAbortSignal]);

    const setLoadedData = useCallback(async (
        response: PresetsBuildResponse,
        cacheKeys: PresetsCacheKeys,
    ) => {
        const relations = response.items;
        const signal = createAbortSignal();

        let roles: Record<string, Role> = {};
        const roleIds = [...new Set(relations
            .filter(({roleId}) => roleId)
            .map(({roleId}) => roleId || ''),
        )];
        const rolesResponse = await apiDispatch(getRoleNamesByIds(
            {requestBody: roleIds},
            {signal},
        ));
        if (rolesResponse.payload.success) {
            roles = rolesResponse.payload.data;
        } else {
            dispatch(presetsActions.onFailure(rolesResponse.payload.data));
            return;
        }

        let grants: Record<string, Grant> = {};
        const grantIds = [...new Set(relations
            .map(({grantId}) => grantId),
        )];
        const grantsResponse = await apiDispatch(getGrantsByIds(
            {requestBody: grantIds},
            {signal},
        ));
        if (grantsResponse.payload.success) {
            grants = grantsResponse.payload.data;
        } else {
            dispatch(presetsActions.onFailure(grantsResponse.payload.data));
            return;
        }

        let resources: Record<string, EntitlementResource> = {};
        const resourceIds = [...new Set(relations
            .map(({resourceId}) => resourceId),
        )];
        const resourcesResponse = await apiDispatch(getResourcesByIds(
            {requestBody: resourceIds},
            {signal},
        ));
        if (resourcesResponse.payload.success) {
            resources = resourcesResponse.payload.data;
        } else {
            dispatch(presetsActions.onFailure(resourcesResponse.payload.data));
            return;
        }

        const presets = Object.fromEntries(response.presets.map(preset => [preset.id, preset]));

        const data = !isEmpty(presets) && relations.length && !isEmpty(grants) && !isEmpty(resources)
            ? getPresetsList({
                    roles,
                    presets,
                    grants,
                    resources,
                    relations,
                    sortDirection: cacheKeys.sortCriteria.direction,
                }) : [];

        dispatch(presetsActions.onSuccess({
            cacheKeys,
            totalCount: response.totalCount,
            data: {
                view: data,
                relations,
            },
        }));
    }, [apiDispatch, dispatch, createAbortSignal]);

    return useCachedTable({
        dataSelector: presetsDataSelector,
        stateSelector: presetsStateSelector,
        resultSelector: presetsResultSelector,
        actions: presetsActions,
        getAction,
        onSuccessLoad: setLoadedData,
    });
};
